import { useEffect, useMemo, useState } from 'react';
import { isSignedIn, logout, onAuthChange, refresh, request } from './api';
import brand from './brand.generated.json';
import { dateTime, Loadable, money, todayIso, useLoad } from './ui';
import { AcceptInvite, Forgot, ResetPassword, SignIn, VerifyEmail } from './views/Auth';
import { Modules, NewOutlet, RegistrationCard, Registrations, RestaurantSetup, RestaurantSignup, type Registration } from './views/Onboarding';
import { Account, Audit, Devices, QrOrdering, Sync, Team } from './views/Manage';
import { Dashboard, LiveOrders, Orders, Reports, type StoreRef } from './views/Store';

function useRoute() {
  const read = () => {
    const [path, query = ''] = window.location.hash.replace(/^#/, '').split('?');
    return { path: path || '/', params: new URLSearchParams(query) };
  };
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => setRoute(read());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}

const go = (path: string) => {
  window.location.hash = path;
};

// Tabs that belong to a licensed module are shown only when the module is enabled.
const STORE_TABS: ReadonlyArray<readonly [string, string, string | null]> = [
  ['dashboard', 'Dashboard', 'cloud.analytics'],
  ['reports', 'Reports', 'cloud.analytics'],
  ['orders', 'Orders', 'cloud.analytics'],
  ['live', 'Live', 'cloud.analytics'],
  ['qr', 'QR Ordering', 'qr.ordering'],
  ['devices', 'Devices', null],
  ['sync', 'Sync & alerts', null],
  ['setup', 'Setup', null],
  ['modules', 'Modules', null],
];

export function App() {
  const route = useRoute();
  const [authed, setAuthed] = useState<boolean | null>(isSignedIn() ? true : null);

  useEffect(() => onAuthChange(setAuthed), []);
  useEffect(() => {
    if (authed === null) void refresh().then(setAuthed);
  }, [authed]);

  const token = route.params.get('token') ?? '';
  if (route.path === '/verify-email') return <VerifyEmail token={token} />;
  if (route.path === '/reset-password') return <ResetPassword token={token} go={go} />;
  if (authed === null) return <div className="center muted">Loading…</div>;
  if (!authed) {
    if (route.path === '/register') return <RestaurantSignup go={go} />;
    if (route.path === '/forgot') return <Forgot />;
    return <SignIn go={(p) => go(route.path === '/accept-invite' ? `/accept-invite?token=${token}` : p)} />;
  }
  if (route.path === '/accept-invite') return <AcceptInvite token={token} go={go} />;
  return <Shell path={route.path} />;
}

function Shell({ path }: { path: string }) {
  const me = useLoad(() => request('GET', '/v1/auth/me'), []);
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem('admin-org'));
  const stores = useLoad(() => (orgId ? request('GET', `/v1/stores?org_id=${orgId}`) : Promise.resolve({ stores: [] })), [orgId]);

  const orgs: { id: string; name: string; role: string }[] = me.data?.user.organizations ?? [];
  useEffect(() => {
    if (orgs.length && (!orgId || !orgs.some((o) => o.id === orgId))) setOrgId(orgs[0].id);
  }, [orgs, orgId]);
  useEffect(() => {
    if (orgId) localStorage.setItem('admin-org', orgId);
  }, [orgId]);

  const regs = useLoad<{ registrations: Registration[] }>(() => request('GET', '/v1/registrations'), [path === '/']);
  const parts = path.split('/').filter(Boolean);
  const storeList: StoreRef[] = stores.data?.stores ?? [];
  const store = parts[0] === 'stores' ? storeList.find((s) => s.id === parts[1]) : undefined;
  const features = useLoad<{ features: Record<string, boolean> }>(() => (store ? request('GET', `/v1/stores/${store.id}/features`) : Promise.resolve({ features: {} })), [store?.id]);
  const enabled = (key: string | null) => !key || features.data?.features?.[key] === true;
  const tab = parts[2] ?? (store && !enabled('cloud.analytics') ? 'setup' : 'dashboard');
  const openRegs = (regs.data?.registrations ?? []).filter((r) => r.status !== 'APPROVED');
  const org = orgs.find((o) => o.id === orgId);

  return (
    <div className="layout">
      <aside className="side">
        <div className="brand-mark">{brand.cloudConsoleName}</div>
        {orgs.length > 1 && (
          <select aria-label="Organization" value={orgId ?? ''} onChange={(e) => { setOrgId(e.target.value); go('/'); }}>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
        <nav>
          <a href="#/" className={path === '/' ? 'active' : ''}>Overview</a>
          <div className="nav-label">Restaurants</div>
          {storeList.map((s) => (
            <a key={s.id} href={`#/stores/${s.id}`} className={store?.id === s.id ? 'active' : ''}>
              <span className={`dot ${s.online ? 'on' : 'off'}`} aria-label={s.online ? 'online' : 'offline'} /> {s.name || 'Unnamed restaurant'}
              {s.status === 'BLOCKED' && <span className="pill critical">Blocked</span>}
            </a>
          ))}
          {openRegs.map((r) => <a key={r.id} href="#/" className="muted">{r.restaurant_name} <span className={`pill ${r.status === 'PENDING' ? 'warning' : 'critical'}`}>{r.status === 'PENDING' ? 'Pending' : r.status === 'REJECTED' ? 'Rejected' : 'Blocked'}</span></a>)}
          <a href="#/register-outlet" className={path === '/register-outlet' ? 'active' : ''}>+ Register restaurant</a>
          <div className="nav-label">Organization</div>
          <a href="#/team" className={path === '/team' ? 'active' : ''}>Team</a>
          {(org?.role === 'owner' || org?.role === 'admin') && <a href="#/audit" className={path === '/audit' ? 'active' : ''}>Audit log</a>}
          <a href="#/account" className={path === '/account' ? 'active' : ''}>Account</a>
          <button className="link" onClick={() => void logout()}>Sign out</button>
        </nav>
      </aside>
      <main className="main">
        <Loadable state={me}>
          {() => {
            if (path === '/register-outlet') return <NewOutlet onDone={() => { regs.reload(); go('/'); }} />;
            if (path === '/team' && orgId) return <><h1>Team</h1><Team orgId={orgId} myRole={org?.role ?? 'viewer'} stores={storeList} /></>;
            if (path === '/audit' && orgId) return <><h1>Audit log</h1><Audit orgId={orgId} /></>;
            if (path === '/account') return <><h1>Account</h1><Account onSignedOut={() => void logout()} /></>;
            if (parts[0] === 'stores') {
              if (stores.loading && !store) return <div className="muted">Loading…</div>;
              if (!store) return <div className="alert error">Restaurant not found.</div>;
              if (store.status === 'BLOCKED') {
                const reg = (regs.data?.registrations ?? []).find((r) => r.restaurant?.id === store.id);
                return reg ? <RegistrationCard r={reg} go={go} onRefresh={regs.reload} /> : <div className="alert error">This restaurant is blocked. Your data is preserved; contact support.</div>;
              }
              return (
                <>
                  <div className="row-between">
                    <h1>{store.name || 'Restaurant'} {store.restaurant_id && <code className="muted small">{store.restaurant_id}</code>}</h1>
                    <span className={`pill ${store.online ? 'good' : 'warning'}`}>{store.online ? 'POS online' : store.pos_devices ? 'POS offline · showing synced data' : 'No POS activated yet'}</span>
                  </div>
                  <div className="tabs" role="tablist">
                    {STORE_TABS.filter(([k, , key]) => enabled(key) && (!['devices', 'setup'].includes(k) || ['owner', 'admin'].includes(store.role)) && (k !== 'qr' || store.role !== 'viewer')).map(([k, label]) => (
                      <a key={k} role="tab" aria-selected={tab === k} href={`#/stores/${store.id}/${k}`}>{label}</a>
                    ))}
                  </div>
                  {tab === 'dashboard' && <Dashboard store={store} />}
                  {tab === 'reports' && <Reports store={store} />}
                  {tab === 'orders' && <Orders store={store} />}
                  {tab === 'live' && <LiveOrders store={store} />}
                  {tab === 'qr' && <QrOrdering store={store} />}
                  {tab === 'devices' && <Devices store={store} />}
                  {tab === 'sync' && <Sync store={store} />}
                  {tab === 'setup' && <RestaurantSetup store={store} />}
                  {tab === 'modules' && <Modules storeId={store.id} />}
                </>
              );
            }
            const approved = storeList.filter((s) => s.status === 'APPROVED');
            if (!approved.length) return <Registrations go={go} />;
            return (
              <>
                {openRegs.map((r) => <RegistrationCard key={r.id} r={r} go={go} onRefresh={regs.reload} />)}
                <Overview orgId={orgId} stores={approved} />
              </>
            );
          }}
        </Loadable>
      </main>
    </div>
  );
}

function Overview({ orgId, stores }: { orgId: string | null; stores: StoreRef[] }) {
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const state = useLoad(() => (orgId ? request('GET', `/v1/reports/overview?org_id=${orgId}&from=${from}&to=${to}`) : Promise.resolve(null)), [orgId, from, to, stores.length]);
  const byId = useMemo(() => new Map(stores.map((s) => [s.id, s])), [stores]);
  if (!stores.length) return null;
  return (
    <>
      <h1>Overview</h1>
      <div className="filters">
        <label>From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      <Loadable state={state}>
        {(d: any) => d && (
          <>
            <div className="stats">
              {Object.entries(d.totals_by_currency).map(([cur, t]: [string, any]) => (
                <div className="stat" key={cur}><div className="stat-label">All restaurants ({cur})</div><div className="stat-value">{money(t.netCollected, cur === 'unknown' ? null : cur)}</div><div className="stat-sub">{t.bills} bills · {t.orders} orders</div></div>
              ))}
            </div>
            <table>
              <thead><tr><th>Restaurant</th><th>Status</th><th>Net collected</th><th>Refunded</th><th>Bills</th><th>Avg bill</th><th>Last sync</th></tr></thead>
              <tbody>{d.stores.map((s: any) => (
                <tr key={s.store_id} className="clickable" onClick={() => go(`/stores/${s.store_id}`)}>
                  <td>{s.name || 'Restaurant'}</td>
                  <td>{byId.get(s.store_id)?.online ? 'Online' : 'Offline'}</td>
                  <td className="num">{money(s.netCollected, s.currency)}</td><td className="num">{money(s.refunded, s.currency)}</td>
                  <td className="num">{s.bills}</td><td className="num">{money(s.averageOrderValue, s.currency)}</td>
                  <td>{dateTime(byId.get(s.store_id)?.last_sync_at)}</td>
                </tr>
              ))}</tbody>
            </table>
            {d.excluded?.length > 0 && <p className="muted small">Not included: {d.excluded.map((x: any) => `${x.name} (${x.reason === 'multi_outlet.reports' ? 'multi-outlet reports not enabled' : x.reason === 'cloud.analytics' ? 'cloud analytics not enabled' : 'unavailable'})`).join(', ')}.</p>}
            <p className="muted small">Dates are each restaurant's own business days in its own timezone.</p>
          </>
        )}
      </Loadable>
    </>
  );
}
